import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { validateTenantScope, type OrganizationId, type TenantScope } from './identity';
import { emitSessionTelemetry } from './telemetry';

// The explicit request scope argument every migrated endpoint takes. 'global'
// is deliberately a symbol-free discriminated object (never a bare string):
// 'global' is a legal organization name, so a string sentinel could collide.
export type RequestScope = { kind: 'organization'; organization: OrganizationId } | { kind: 'global' };

export const GLOBAL_SCOPE: RequestScope = Object.freeze({ kind: 'global' });

export const requestScopeFor = (organization: OrganizationId): RequestScope => ({ kind: 'organization', organization });

class MissingRequestScopeError extends Error {
  constructor() {
    super('A scoped request reached the transport without a tenant scope');
    this.name = 'MissingRequestScopeError';
  }
}

const toTenantScope = (transport: 'api' | 'channelApi', path: string, scope: RequestScope): TenantScope => {
  // Fail closed at runtime: branded types prove intent at compile time for
  // TypeScript callers, but 42 JS files are never type-checked, so the
  // transport re-validates every scope it is handed.
  if (!scope || (scope.kind !== 'organization' && scope.kind !== 'global')) {
    emitSessionTelemetry({ code: 'organization_scope_missing', transport, path });
    throw new MissingRequestScopeError();
  }
  if (scope.kind === 'global') return validateTenantScope({ kind: 'none' });
  try {
    return validateTenantScope({ kind: 'organization', organization: scope.organization });
  } catch {
    emitSessionTelemetry({ code: 'organization_scope_invalid', transport, path });
    throw new MissingRequestScopeError();
  }
};

export type ScopedClient = {
  get<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  delete<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  head<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = unknown>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  put<T = unknown>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  patch<T = unknown>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
};

// Wraps an axios instance so every request carries the given scope's tenant
// semantics explicitly. The session interceptors honor `organizationScope`
// and never fall back to the ambient seam when it is present.
export const scopedClientFor = (transport: 'api' | 'channelApi', instance: AxiosInstance, scope: RequestScope): ScopedClient => {
  const withScope = (path: string, config: AxiosRequestConfig | undefined): AxiosRequestConfig => ({
    ...config,
    organizationScope: toTenantScope(transport, path, scope),
  });

  // Methods are async so a fail-closed scope violation surfaces as a
  // rejection (what every caller's try/catch expects), not a sync throw.
  return {
    get: async (path, config) => instance.get(path, withScope(path, config)),
    delete: async (path, config) => instance.delete(path, withScope(path, config)),
    head: async (path, config) => instance.head(path, withScope(path, config)),
    post: async (path, data, config) => instance.post(path, data, withScope(path, config)),
    put: async (path, data, config) => instance.put(path, data, withScope(path, config)),
    patch: async (path, data, config) => instance.patch(path, data, withScope(path, config)),
  };
};
